/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createEscalationStep } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type EscalationStepDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function EscalationStepDialog({ actionDialogReturn, onNavigate: _onNavigate }: EscalationStepDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { setWorkspaceVersion } = useWorkspace();
  const { toast } = useFeedback();

  if (!actionDialog || actionDialog.kind !== 'escalation-step') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const stepOrderNumber = Number.parseInt(actionDialog.stepOrder, 10);
  const timeoutNumber = Number.parseInt(actionDialog.timeoutSeconds, 10);
  const canSubmit =
    Number.isFinite(stepOrderNumber) &&
    stepOrderNumber >= 1 &&
    Number.isFinite(timeoutNumber) &&
    timeoutNumber >= 0 &&
    (actionDialog.targetType !== 'specific_identity' || actionDialog.targetWorkIdentityId.trim().length > 0);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'escalation-step' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      await createEscalationStep(session.accessToken, {
        escalationPolicyId: actionDialog.escalationPolicyId,
        stepOrder: stepOrderNumber,
        channel: actionDialog.channel,
        timeoutSeconds: timeoutNumber,
        targetType: actionDialog.targetType,
        targetWorkIdentityId:
          actionDialog.targetType === 'specific_identity'
            ? actionDialog.targetWorkIdentityId.trim()
            : undefined
      });
      setWorkspaceVersion((v) => v + 1);
      toast('success', `Step added to "${actionDialog.policyLabel}"`);
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">On-call</div>
            <h3>Add escalation step</h3>
            <p>Add one ordered notification step to &quot;{actionDialog.policyLabel}&quot;.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <div className="settingsSplit">
            <label className="settingsField">
              <span>Step order</span>
              <input
                type="number"
                min={1}
                max={100}
                value={actionDialog.stepOrder}
                onChange={(e) => setActionDialog({ ...actionDialog, stepOrder: e.target.value })}
                autoFocus
              />
            </label>
            <label className="settingsField">
              <span>Timeout (seconds)</span>
              <input
                type="number"
                min={0}
                max={86400}
                value={actionDialog.timeoutSeconds}
                onChange={(e) => setActionDialog({ ...actionDialog, timeoutSeconds: e.target.value })}
              />
              <p className="fieldHint">How long to wait before escalating to the next step.</p>
            </label>
          </div>
          <label className="settingsField">
            <span>Channel</span>
            <select
              value={actionDialog.channel}
              onChange={(e) => setActionDialog({ ...actionDialog, channel: e.target.value as typeof actionDialog.channel })}
            >
              <option value="in_app">In-app</option>
              <option value="email">Email</option>
            </select>
          </label>
          <label className="settingsField">
            <span>Target</span>
            <select
              value={actionDialog.targetType}
              onChange={(e) => setActionDialog({ ...actionDialog, targetType: e.target.value as typeof actionDialog.targetType })}
            >
              <option value="on_call">Whoever is on call</option>
              <option value="specific_identity">Specific identity</option>
            </select>
          </label>
          {actionDialog.targetType === 'specific_identity' ? (
            <label className="settingsField">
              <span>Target work identity ID</span>
              <input
                value={actionDialog.targetWorkIdentityId}
                onChange={(e) => setActionDialog({ ...actionDialog, targetWorkIdentityId: e.target.value })}
                placeholder="UUID of the work identity to notify"
              />
            </label>
          ) : null}
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy || !canSubmit}>
            {dialogBusy ? 'Adding…' : 'Add step'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { EscalationStepDialog };
